"use client";

import React from "react";
import { Target } from "lucide-react";

interface SeoChipProps {
  text: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** Pass "dark" when the chip sits on a dark/coloured background */
  variant?: "light" | "dark";
  className?: string;
}

/**
 * Single-source SEO chip badge used as a section eyebrow across all pages.
 * All sections must use this component — never roll custom chip markup inline.
 */
export default function SeoChip({
  text,
  icon: Icon = Target,
  variant = "light",
  className = "",
}: SeoChipProps) {
  const isDark = variant === "dark";
  const bg     = isDark ? "bg-white/20"       : "bg-abis-accent/10";
  const border = isDark ? "border-white/30"   : "border-abis-accent/20";
  const color  = isDark ? "text-white"        : "text-abis-accent";

  return (
    <p
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 md:px-4 md:py-2 mb-6 ${bg} ${border} ${className}`}
    >
      <Icon className={`h-3.5 w-3.5 shrink-0 md:h-4 md:w-4 ${color}`} aria-hidden />
      <span className={`text-xs font-semibold uppercase tracking-wide md:text-sm ${color}`}>
        {text}
      </span>
    </p>
  );
}
